import { Injectable } from '@angular/core';
import { IUser } from '@shared/interfaces/user/IUser';
import { ApiService } from '@shared/services/api.service';
import { LocalService } from '@shared/services/local.service';
import { GuardService } from '@shared/services/guard.service';
import { IDashboardItems, scdUserConverter } from '@shared/interfaces';
import { IPaginatedResult } from '@shared/interfaces/mailing/IPaginatedResult';
import { convertQueryToParams } from '@shared/helpers/query-converter';
import { IQueryUser } from '@shared/interfaces/queries/IQueryUser';
import { IUpdateUser } from '@shared/interfaces/user/IUpdateUser';

const CURRENT_USER = 'current_user';
const IMPERSONATION = 'impersonation';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    userPromise: Promise<IUser>;

    constructor(
        private apiService: ApiService,
        private localService: LocalService,
        private guardService: GuardService
    ) {}

    public getMyUser(): Promise<IUser> {
        if (this.userPromise) {
            return this.userPromise;
        }
        return (this.userPromise = new Promise<IUser>((resolve, reject) => {
            this.apiService
                .get('/users/me')
                .then(user => {
                    this.guardService.currentUser = user;
                    this.localService.set(CURRENT_USER, user);
                    resolve(user);
                })
                .catch(e => {
                    reject(e);
                });
        }));
    }

    public getAllUsers(): Promise<IPaginatedResult<IUser>> {
        return this.apiService.get('/users');
    }

    getAllUsersFlat(query: IQueryUser): Promise<IPaginatedResult<IUser>> {
        return this.apiService.get(`/users/flat${convertQueryToParams<IQueryUser>(query)}`);
    }

    getById(id: string): Promise<IUser> {
        return this.apiService.get('/users/' + encodeURIComponent(id));
    }

    public searchUsers(query: string): Promise<IUser[]> {
        return this.apiService.get('/users?query=' + query);
    }

    createUser(item: IUpdateUser): Promise<IUser> {
        item.userData = scdUserConverter.toApi(item.userData as any);
        return this.apiService.post('/users', item);
    }

    public updateUser(user: IUpdateUser): Promise<IUser> {
        return this.apiService.patch('/users', user);
    }

    async updateUsersFromScd(users: IUpdateUser[]): Promise<IUser[]> {
        return this.apiService.patch('/users/scd', users);
    }

    public deleteUser(user: IUser): Promise<boolean> {
        return this.apiService.delete('/users/' + user.id);
    }

    public getHomeItems(limit = 8): Promise<IDashboardItems> {
        return this.apiService.get(`/site/dashboard?limit=${limit}`);
    }

    public setImpersonation(id: string): void {
        this.localService.set(IMPERSONATION, id);
        this.localService.remove(CURRENT_USER);
    }

    public removeImpersonation(): void {
        this.localService.remove(IMPERSONATION);
        this.localService.remove(CURRENT_USER);
    }

    public async updateLanguage(language: string): Promise<void> {
        try {
            const user = await this.apiService.patch(`/users/${this.guardService.currentUser.id}/settings`, {
                language
            });
            this.guardService.currentUser = user;
            this.localService.set(CURRENT_USER, user);
        } catch (e) {
            console.error(e);
        }
    }
}
